import { useState } from 'preact/hooks';
import { platformUsers, UserSearchResult } from './adminApi';

export function UsersSearchPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function search(e: Event) {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await platformUsers.search(query.trim(), 50);
      setResults(r.results || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>User lookup</h1>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Search across photographer users, consumer accounts, and platform staff in one query.
        Matches email or name.
      </p>

      <form onSubmit={search} style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <input
          placeholder="Email or name"
          value={query}
          autoFocus
          onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          style={{ flex: 1 }}
        />
        <button class="primary" type="submit" disabled={loading || !query.trim()}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {err && <div class="error-banner">{err}</div>}

      {results && results.length === 0 && (
        <p style={{ color: 'var(--admin-text-dim)' }}>No users found.</p>
      )}

      {results && results.length > 0 && (
        <table class="admin-table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Name</th>
              <th>Email</th>
              <th>Status / role</th>
              <th>Org memberships</th>
              <th>Last login</th>
            </tr>
          </thead>
          <tbody>
            {results.map((u) => (
              <tr key={`${u.kind}:${u.id}`}>
                <td>
                  <span
                    class="badge"
                    style={{
                      background:
                        u.kind === 'USER_KIND_PLATFORM'
                          ? 'rgba(90, 167, 255, 0.15)'
                          : u.kind === 'USER_KIND_PHOTOGRAPHER'
                          ? 'rgba(79, 209, 139, 0.15)'
                          : 'rgba(240, 180, 41, 0.15)',
                      color:
                        u.kind === 'USER_KIND_PLATFORM'
                          ? 'var(--admin-accent)'
                          : u.kind === 'USER_KIND_PHOTOGRAPHER'
                          ? 'var(--admin-success)'
                          : 'var(--admin-warning)',
                    }}
                  >
                    {kindLabel(u.kind)}
                  </span>
                </td>
                <td>{u.name || '—'}</td>
                <td class="mono">{u.email}</td>
                <td class="mono">
                  {u.status}
                  {u.platformRole && ` · ${u.platformRole}`}
                </td>
                <td>
                  {u.orgMemberships && u.orgMemberships.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {u.orgMemberships.map((om) => (
                        <li key={om.orgId}>
                          {om.orgName} <span style={{ color: 'var(--admin-text-dim)' }}>({om.role})</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span style={{ color: 'var(--admin-text-dim)' }}>—</span>
                  )}
                </td>
                <td class="mono">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function kindLabel(k: string): string {
  switch (k) {
    case 'USER_KIND_PLATFORM':
      return 'platform';
    case 'USER_KIND_PHOTOGRAPHER':
      return 'photographer';
    case 'USER_KIND_CONSUMER':
      return 'consumer';
    default:
      return 'unknown';
  }
}
